import React, { useState, useEffect, useMemo } from "react";
import {
  Stack,
  Title,
  Text,
  Group,
  Divider,
  Paper,
  Button,
  Image,
  Box,
  Badge,
  Modal,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconPackage,
  IconPercentage,
  IconCalendar,
  IconClock,
  IconGiftCard,
} from "@tabler/icons-react";
import moment from "moment-timezone";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { useProducts } from "../../../../../context/ProductsContext";
import { useSalon } from "../../../../../context/SalonContext";
import { QuickSaleProductState } from "../../../../../types/quickSale";
import { DiscountPreset } from "../../../../../types/discounts";
import {
  Client,
  PendingGiftCardLineItem,
} from "../../../../../types/transactions";
import ProductLookup from "../TransactionDetailModal/ProductLookup";
import DeleteConfirmationModal from "../../../../common/DeleteConfirmationModal";
import ProductDetailCard from "../../../../common/ProductDetailCard";
import ApplyDiscountModal from "../TransactionDetailModal/ApplyDiscountModal";
import QuickDiscountsSection from "../TransactionDetailModal/QuickDiscountsSection";
import ProductForm from "../TransactionDetailModal/ProductForm";
import { getDefaultCommissionTarget } from "../../../../../utils/commissionUtils";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import ProductCommissionLine from "../../../../common/ProductCommissionLine";
import usePermissions from "../../../../../hooks/usePermissions";
import { formatDiscountValue } from "../../../../../utils/discountFormatting";
import {
  buildRemoveGroupChanges,
  DiscountGroup,
  groupLineDiscounts,
  LineDiscountChange,
} from "../../../../../utils/lineItemDiscounts";
import {
  cartLineDiscountDollars,
  cartLineDiscountLabels,
  cartLineHasDiscount,
  cartLineSubtotal,
  cartProductToDiscountableLine,
} from "../../../../../utils/quickSaleCart";

interface QuickSaleDetailsProps {
  products: QuickSaleProductState[];
  client?: Client;
  onAddProduct: (product: QuickSaleProductState) => void;
  onUpdateProduct: (
    productId: string,
    updates: Partial<QuickSaleProductState>,
  ) => void;
  onRemoveProduct: (productId: string) => void;
  isViewOnly?: boolean;
  transactionDate?: string; // ISO date string for existing transactions
  timezone?: string; // Salon timezone for date formatting
  /** Gift Cards feature wall — surfaces the "Gift Cards" section. Defaults false. */
  giftCardsEnabled?: boolean;
  /** Gift-card line items already on the (real) transaction. */
  giftCardLineItems?: PendingGiftCardLineItem[];
  /** Whether a gift card can be added yet — only once a real transaction exists
   *  (a gift card has no product_uuid, so it can't be the first item). */
  canAddGiftCard?: boolean;
  onAddGiftCard?: () => void;
  onRemoveGiftCard?: (lineItemId: string) => void;
}

export default function QuickSaleDetails({
  products,
  client,
  onAddProduct,
  onUpdateProduct,
  onRemoveProduct,
  isViewOnly = false,
  transactionDate,
  timezone = "America/New_York",
  giftCardsEnabled = false,
  giftCardLineItems = [],
  canAddGiftCard = false,
  onAddGiftCard,
  onRemoveGiftCard,
}: QuickSaleDetailsProps) {
  const { isMobile } = useResponsive();
  const { getProductById, setStatusFilter } = useProducts();
  const { employees, currentUser } = useSalon() as {
    employees: any[];
    currentUser: any;
  };
  const canAssignProductCommission =
    usePermissions()?.settingsCommissions?.canAssignProductCommission === true;

  // Set filter to show only active products
  useEffect(() => {
    setStatusFilter("Active");
  }, [setStatusFilter]);

  // Product add modal state
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [staffCommissionTarget, setStaffCommissionTarget] = useState<
    string | null | undefined
  >(undefined);

  // Product edit modal state
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);

  // Delete confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [productToDelete, setProductToDelete] =
    useState<QuickSaleProductState | null>(null);

  // Discount modal state: stacked discounts on cart lines via the shared
  // ApplyDiscountModal ("Change" edits a group; otherwise a new discount).
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [discountGroupToChange, setDiscountGroupToChange] =
    useState<DiscountGroup | null>(null);
  // The quick discount whose "+ Add" opened the modal (preselected on every
  // cart line); null otherwise.
  const [quickDiscountPreset, setQuickDiscountPreset] =
    useState<DiscountPreset | null>(null);
  const cartLines = useMemo(
    () => products.map(cartProductToDiscountableLine),
    [products],
  );
  const cartDiscountGroups = useMemo(
    () => groupLineDiscounts(cartLines),
    [cartLines],
  );

  // Create a mock transaction structure for the ProductForm edit modal
  const mockTransaction = useMemo(() => {
    return {
      transaction: {
        id: "mock-transaction-id",
        appointment_status: null,
      },
      client: client || { id: "", first_name: "", last_name: "" },
      line_items: {
        booked_services: [], // Quick sales don't have services
        products: products.map((product) => ({
          id: product.productId,
          line_item_id: product.productId,
          name: product.productName,
          quantity: product.quantity,
          unit_price: product.unitPrice,
          img_url: product.imgUrl || "",
          staff_commission_target: product.staffCommissionTarget || null,
        })),
      },
      payments: [],
    };
  }, [products, client]);

  // Format amount helper — keeps the "0 renders as a dash" behavior while
  // delegating the actual money formatting to formatCurrency.
  const formatAmount = (amount: number): string => {
    return amount === 0 ? "-" : formatCurrency(amount);
  };

  // Handle add product modal
  const handleOpenAddProductModal = () => {
    setSelectedProductId("");
    setQuantity(1);
    // Quick sales have no service, so the default commission target is the
    // logged-in seller. It can be reassigned per cart row afterward.
    const initialTarget = getDefaultCommissionTarget(
      undefined,
      currentUser?.id,
    );
    setStaffCommissionTarget(initialTarget);
    setIsAddProductModalOpen(true);
  };

  const handleCloseAddProductModal = () => {
    setIsAddProductModalOpen(false);
    setSelectedProductId("");
    setQuantity(1);
    setStaffCommissionTarget(undefined);
  };

  // Handle edit product modal
  const handleOpenEditProductModal = (product: QuickSaleProductState) => {
    // Convert QuickSaleProductState to transaction product format
    const transactionProduct = {
      id: product.productId,
      line_item_id: product.productId,
      name: product.productName,
      quantity: product.quantity,
      unit_price: product.unitPrice,
      img_url: product.imgUrl || "",
      staff_commission_target: product.staffCommissionTarget,
    };
    setEditingProduct(transactionProduct);
    setIsEditProductModalOpen(true);
  };

  const handleCloseEditProductModal = () => {
    setIsEditProductModalOpen(false);
    setEditingProduct(null);
  };

  const handleProductEdited = (data: any) => {
    // Extract the updated product data
    const updatedProduct = data.line_items.products[0];

    // Update the product in the QuickSale state
    onUpdateProduct(updatedProduct.id, {
      quantity: updatedProduct.quantity,
      staffCommissionTarget:
        updatedProduct.staff_commission_target !== undefined
          ? updatedProduct.staff_commission_target
          : undefined,
    });

    handleCloseEditProductModal();
  };

  // Handle delete product
  const handleDeleteProduct = (product: QuickSaleProductState) => {
    setProductToDelete(product);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      onRemoveProduct(productToDelete.productId);
    }
    setShowDeleteConfirmation(false);
    setProductToDelete(null);
  };

  const handleCloseDiscountModal = () => {
    setIsDiscountModalOpen(false);
    setDiscountGroupToChange(null);
    setQuickDiscountPreset(null);
  };

  // Open the apply modal fresh, or on a group to change it.
  const openApplyDiscount = (group: DiscountGroup | null = null) => {
    setDiscountGroupToChange(group);
    setQuickDiscountPreset(null);
    setIsDiscountModalOpen(true);
  };

  // A quick discount's "+ Add": the preset preselected on every cart line.
  const openQuickDiscount = (preset: DiscountPreset) => {
    setDiscountGroupToChange(null);
    setQuickDiscountPreset(preset);
    setIsDiscountModalOpen(true);
  };

  // Each changed cart line takes its full next discount set.
  const applyCartDiscountChanges = (changes: LineDiscountChange[]) => {
    changes.forEach((change) =>
      onUpdateProduct(change.lineKey, { discounts: change.discounts }),
    );
  };

  // Calculate line total with discount
  const calculateLineTotal = (product: QuickSaleProductState) =>
    cartLineSubtotal(product) - cartLineDiscountDollars(product);

  const noDiscountsPlaceholder = (
    <Paper
      p="md"
      withBorder
      style={{ borderColor: "#e8e8e8", borderStyle: "dashed" }}
    >
      <Stack gap="sm" align="center">
        <IconPercentage size={32} color="#c1c2c5" />
        <Text size="md" c="dimmed" ta="center">
          No discounts applied
        </Text>
      </Stack>
      {!isViewOnly && (
        <QuickDiscountsSection
          lines={cartLines}
          onAdd={openQuickDiscount}
          withDivider
        />
      )}
    </Paper>
  );

  // One row per discount group on the cart, with Change/Remove.
  const renderStackedCartDiscounts = () => {
    if (cartDiscountGroups.length === 0) return noDiscountsPlaceholder;
    return (
      <Stack gap="md">
        {cartDiscountGroups.map((group) => (
          <Paper key={group.id} p="md" withBorder>
            <Group justify="space-between" align="flex-start">
              <Stack gap="xs" style={{ flex: 1 }}>
                <Text fw={500}>
                  {group.name} -{" "}
                  {formatDiscountValue(group.discount_type, group.amount)}
                </Text>
                <Text size="sm" c="dimmed">
                  Applies to{" "}
                  {cartLines
                    .filter((line) => group.lineKeys.includes(line.key))
                    .map((line) => line.name)
                    .join(", ")}
                </Text>
              </Stack>
              {!isViewOnly && (
                <Group gap="xs">
                  <Button
                    variant="subtle"
                    size="xs"
                    aria-label={`Change discount ${group.name}`}
                    onClick={() => openApplyDiscount(group)}
                  >
                    <IconEdit size={14} />
                  </Button>
                  <Button
                    variant="subtle"
                    size="xs"
                    color="var(--color-danger)"
                    aria-label={`Remove discount ${group.name}`}
                    onClick={() =>
                      applyCartDiscountChanges(
                        buildRemoveGroupChanges(cartLines, group),
                      )
                    }
                  >
                    <IconTrash size={14} />
                  </Button>
                </Group>
              )}
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  };

  return (
    <>
      <Stack gap="lg" px={isMobile ? "md" : "xl"} py="xl">
        {/* Header Section */}
        <Stack gap="xs">
          <Group justify="space-between">
            <Title order={3}>
              Quick Sale
              {client && ` for ${client.first_name} ${client.last_name}`}
            </Title>
            {isViewOnly && (
              <Badge color="green" variant="light" size="sm">
                Completed
              </Badge>
            )}
          </Group>

          {/* Date/Time display for existing transactions */}
          {transactionDate && (
            <Group>
              <Group gap="xs" display={isMobile ? "none" : "inherit"}>
                <IconCalendar size={16} color="#c1c2c5" />
                <Text size="sm" c="dimmed">
                  {moment
                    .utc(transactionDate)
                    .tz(timezone)
                    .format("MMM DD, YYYY")}
                </Text>
              </Group>
              <Group gap="xs">
                <IconClock size={16} color="#c1c2c5" />
                <Text size="sm" c="dimmed">
                  {isMobile &&
                    moment
                      .utc(transactionDate)
                      .tz(timezone)
                      .format("M/D/YY")}{" "}
                  {moment.utc(transactionDate).tz(timezone).format("h:mm A")}
                </Text>
              </Group>
            </Group>
          )}
        </Stack>

        <Divider />

        {/* Products Section */}
        <div>
          <Group justify="space-between" mb={isMobile ? "xs" : "md"}>
            <Title order={4}>Products</Title>
            {!isViewOnly && (
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconPlus size={16} />}
                onClick={handleOpenAddProductModal}
              >
                Add Product
              </Button>
            )}
          </Group>
          {products.length > 0 ? (
            <Stack gap="md">
              {products.map((product) => (
                <Paper key={product.productId} p="md" withBorder>
                  <Group justify="space-between" align="flex-start">
                    {product.imgUrl ? (
                      <Image
                        src={product.imgUrl}
                        w={60}
                        h={60}
                        fit="contain"
                        alt={product.productName}
                      />
                    ) : (
                      <Box
                        w={60}
                        h={60}
                        bg="gray.2"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                        }}
                      >
                        <IconPackage size={30} color="gray.6" />
                      </Box>
                    )}
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Text fw={500}>{product.productName}</Text>
                      <Text size="sm" c="dimmed">
                        Quantity: {product.quantity}
                      </Text>
                      <ProductCommissionLine
                        staffCommissionTarget={product.staffCommissionTarget}
                        employees={employees}
                        canEdit={canAssignProductCommission && !isViewOnly}
                        onSave={(staffId) =>
                          onUpdateProduct(product.productId, {
                            staffCommissionTarget: staffId,
                          })
                        }
                      />
                      {cartLineDiscountLabels(product).map((label) => (
                        <Badge
                          key={label}
                          variant="light"
                          color="limeGreen"
                          size="sm"
                        >
                          {label}
                        </Badge>
                      ))}
                    </Stack>
                    <Stack gap="xs" align="flex-end">
                      {cartLineHasDiscount(product) ? (
                        <Stack gap={2} align="flex-end">
                          <Text
                            size="sm"
                            c="dimmed"
                            style={{ textDecoration: "line-through" }}
                          >
                            {formatAmount(product.unitPrice * product.quantity)}
                          </Text>
                          <Text fw={500} c="red">
                            {formatAmount(calculateLineTotal(product))}
                          </Text>
                        </Stack>
                      ) : (
                        <Text fw={500}>
                          {formatAmount(product.unitPrice * product.quantity)}
                        </Text>
                      )}
                      {!isViewOnly && (
                        <Group gap="xs">
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => handleOpenEditProductModal(product)}
                          >
                            <IconEdit size={14} />
                          </Button>
                          <Button
                            variant="subtle"
                            size="xs"
                            color="red"
                            onClick={() => handleDeleteProduct(product)}
                          >
                            <IconTrash size={14} />
                          </Button>
                        </Group>
                      )}
                    </Stack>
                  </Group>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper
              p="md"
              withBorder
              style={{ borderColor: "#e8e8e8", borderStyle: "dashed" }}
            >
              <Stack gap="sm" align="center">
                <IconPackage size={32} color="#c1c2c5" />
                <Text size="md" c="dimmed" ta="center">
                  No products added yet
                </Text>
              </Stack>
            </Paper>
          )}
        </div>

        {/* Gift Cards Section — feature-walled. A gift card can only be added
            once a real transaction exists (products create it first), so the
            action is disabled with a hint until then. */}
        {giftCardsEnabled && (
          <div>
            <Group justify="space-between" mb={isMobile ? "xs" : "md"}>
              <Title order={4}>Gift Cards</Title>
              {!isViewOnly && (
                <Tooltip
                  label="Add a product first to start the sale, then add a gift card."
                  disabled={canAddGiftCard}
                  multiline
                  w={240}
                  withArrow
                  events={{ hover: true, focus: true, touch: true }}
                >
                  <Box>
                    <Button
                      variant="subtle"
                      size="sm"
                      leftSection={<IconPlus size={16} />}
                      onClick={onAddGiftCard}
                      disabled={!canAddGiftCard}
                    >
                      Add Gift Card
                    </Button>
                  </Box>
                </Tooltip>
              )}
            </Group>
            {giftCardLineItems.length > 0 && (
              <Stack gap="md">
                {giftCardLineItems.map((giftCard) => (
                  <Paper key={giftCard.line_item_id} p="md" withBorder>
                    <Group justify="space-between" align="flex-start">
                      <Box
                        w={60}
                        h={60}
                        bg="gray.2"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                        }}
                      >
                        <IconGiftCard size={30} color="gray.6" />
                      </Box>
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Text fw={500}>{giftCard.name}</Text>
                        <Text size="sm" c="dimmed">
                          {giftCard.code
                            ? `Code: ${giftCard.code}`
                            : "Code auto-generated at payment"}
                        </Text>
                      </Stack>
                      <Stack gap="xs" align="flex-end">
                        <Text fw={500}>{formatAmount(giftCard.amount)}</Text>
                        {!isViewOnly && onRemoveGiftCard && (
                          <Button
                            variant="subtle"
                            size="xs"
                            color="red"
                            aria-label={`Remove ${giftCard.name}`}
                            onClick={() =>
                              onRemoveGiftCard(giftCard.line_item_id)
                            }
                          >
                            <IconTrash size={14} />
                          </Button>
                        )}
                      </Stack>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </div>
        )}

        {/* Discounts Section */}
        <div>
          <Group justify="space-between" mb={isMobile ? "xs" : "md"}>
            <Title order={4}>Discounts</Title>
            {!isViewOnly && (
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconPlus size={16} />}
                onClick={() => openApplyDiscount()}
                disabled={products.length === 0}
              >
                Add Discount
              </Button>
            )}
          </Group>
          {renderStackedCartDiscounts()}
        </div>
      </Stack>

      {/* Add Product Modal */}
      <Modal
        opened={isAddProductModalOpen}
        onClose={handleCloseAddProductModal}
        title="Add Product"
        size="lg"
        centered
      >
        <Stack gap="md" p="md">
          <ProductLookup
            value={selectedProductId}
            onChange={setSelectedProductId}
          />

          {selectedProductId &&
            (() => {
              const selectedProduct = getProductById(selectedProductId);
              return (
                selectedProduct && (
                  <>
                    <ProductDetailCard
                      product={selectedProduct}
                      quantity={quantity}
                      onQuantityChange={(val) => setQuantity(val)}
                    />

                    <Divider />

                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Total Price:
                      </Text>
                      <Text size="lg" fw={500}>
                        {formatCurrency(selectedProduct.list_price * quantity)}
                      </Text>
                    </Group>
                  </>
                )
              );
            })()}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleCloseAddProductModal}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const selectedProduct = getProductById(selectedProductId);
                if (!selectedProduct) return;

                const newProduct: QuickSaleProductState = {
                  productId: selectedProduct.id,
                  productName: selectedProduct.name,
                  quantity: quantity,
                  unitPrice: selectedProduct.list_price,
                  imgUrl: selectedProduct.img_url,
                  availableStock: selectedProduct.quantity,
                  discounts: [],
                  staffCommissionTarget:
                    staffCommissionTarget !== undefined
                      ? staffCommissionTarget
                      : undefined,
                };

                onAddProduct(newProduct);
                handleCloseAddProductModal();
              }}
              disabled={!selectedProductId || quantity <= 0}
              color="var(--color-primary)"
            >
              Add Product
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Product Modal */}
      {editingProduct && (
        <Modal
          opened={isEditProductModalOpen}
          onClose={handleCloseEditProductModal}
          title="Edit Product"
          size="lg"
          fullScreen={isMobile}
          centered
        >
          <ProductForm
            mode="edit"
            product={editingProduct}
            transaction={mockTransaction as any}
            onSubmit={handleProductEdited}
            onCancel={handleCloseEditProductModal}
          />
        </Modal>
      )}

      {/* Discount Modal */}
      <ApplyDiscountModal
        opened={isDiscountModalOpen}
        onClose={handleCloseDiscountModal}
        lines={cartLines}
        editingGroup={discountGroupToChange}
        initialPreset={quickDiscountPreset}
        onApply={applyCartDiscountChanges}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Product"
        message={`Are you sure you want to remove ${productToDelete?.productName} from this sale?`}
        isLoading={false}
      />
    </>
  );
}
